import { useCallback, useEffect } from 'react';
import axios from 'axios';
import { msalAuthService } from './msal-native-auth';
import { translateEntraError } from './entra-errors';
import type { AuthStore, AuthUser } from './auth-store-factory';
import type { CustomAuthAccountData } from '@azure/msal-browser/custom-auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface MslSignInResult {
  isFailed(): boolean;
  isCompleted(): boolean;
  data?: CustomAuthAccountData;
  error?: { errorData?: { errorDescription?: string } };
}

interface MslGetAccountResult {
  isCompleted(): boolean;
  data?: CustomAuthAccountData;
}

export interface AuthHookConfig {
  store: () => AuthStore;
  storageKey: string;
  validateUser: (user: AuthUser) => boolean;
  mapUserData: (userData: Record<string, unknown>) => AuthUser;
  logPrefix: string;
}

export interface AuthHookResult {
  isAuthenticated: boolean;
  user: AuthUser | null;
  login: (identifier: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loading: boolean;
  getAccessToken: () => Promise<string | null>;
}

async function handleSignInSuccess(
  authData: CustomAuthAccountData,
  config: AuthHookConfig,
  store: AuthStore,
): Promise<{ success: boolean; error?: string }> {
  const idToken = authData.getIdToken() || '';
  const account = authData.getAccount();

  const response = await axios.get(`${API_BASE_URL}/api/core/v1/auth/me`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });

  if (response.data.success && response.data.user_data) {
    const claims = account?.idTokenClaims || {};
    let tenantId = claims.tenantId || claims.tenant_id;
    if (!tenantId) {
      for (const key of Object.keys(claims)) {
        if (typeof key === 'string' && (key.endsWith('_tenantId') || key.endsWith('_tenant_id'))) {
          tenantId = claims[key];
          break;
        }
      }
    }

    const mergedData = {
      ...response.data.user_data,
      ...claims,
      // JWT claims are primary; DB tenant_id is the fallback
      tenant_id: tenantId || (response.data.user_data as Record<string, unknown>).tenant_id,
    };

    const user = config.mapUserData(mergedData);

    if (!config.validateUser(user)) {
      return { success: false, error: getAccessDeniedMessage(user) };
    }

    store.setUser(user);
    store.setToken(idToken);
    store.setRefreshToken(account?.idToken || '');
    store.setIsAuthenticated(true);

    return { success: true };
  }

  return { success: false, error: response.data.error || 'Sync failed' };
}

export function createAuthHook(config: AuthHookConfig): () => AuthHookResult {
  return () => {
    const store = config.store();

    const getAccessToken = useCallback(async (): Promise<string | null> => {
      return store.token;
    }, [store]);

    const login = async (
      identifier: string,
      password: string,
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        const client = await msalAuthService.getClient();

        const accountResult = client.getCurrentAccount() as unknown as MslGetAccountResult;
        if (accountResult.isCompleted() && accountResult.data) {
          try { await accountResult.data.signOut(); } catch { /* ignore */ }
        }

        const normalizedIdentifier = normalizeIdentifierForLogin(identifier);

        const signInResult = await client.signIn({
          username: normalizedIdentifier,
          password,
        }) as unknown as MslSignInResult;

        if (signInResult.isFailed()) {
          const rawError = signInResult.error?.errorData?.errorDescription;
          return { success: false, error: translateEntraError(rawError) };
        }

        if (signInResult.isCompleted() && signInResult.data) {
          return await handleSignInSuccess(signInResult.data, config, store);
        }

        return { success: false, error: 'Multi-step authentication not yet supported' };
      } catch (error: unknown) {
        return { success: false, error: extractErrorMessage(error) };
      }
    };

    const logout = useCallback(async () => {
      try {
        const client = await msalAuthService.getClient();
        const accountResult = client.getCurrentAccount() as unknown as MslGetAccountResult;
        if (accountResult.isCompleted() && accountResult.data) {
          await accountResult.data.signOut();
        }
      } catch {
        // MSAL signOut may fail; continue with local cleanup
      }
      store.reset();
      localStorage.removeItem(config.storageKey);
    }, [store]);

    const isAuthenticated = store.isAuthenticated;
    const user = store.user;
    const loading = !store.hasHydrated;

    useEffect(() => {
      if (store.hasHydrated && user && !config.validateUser(user)) {
        logout();
      }
    }, [store.hasHydrated, user, logout]);

    return { isAuthenticated, user, login, logout, loading, getAccessToken };
  };
}

function getAccessDeniedMessage(user: AuthUser): string {
  if (user.type === 'secretary') {
    return 'Secretárias devem utilizar o aplicativo mobile. Este portal é exclusivo para profissionais de saúde.';
  }
  if (user.type === 'patient') {
    return 'Acesso não autorizado para este tipo de usuário. Pacientes não têm acesso ao portal web, apenas profissionais de saúde.';
  }
  if (user.type === 'doctor' || user.type === 'professional') {
    return 'Esta página é exclusiva para contas de pacientes. Profissionais de saúde devem excluir sua conta pelas configurações de assinatura no portal.';
  }
  if (!user.system_role) {
    return 'Access not authorized. Platform admin role required.';
  }
  return 'Acesso não autorizado para este tipo de usuário.';
}

function extractErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
    if (axiosError.response?.data?.error) {
      return translateEntraError(axiosError.response.data.error);
    }
    if (axiosError.message) {
      return translateEntraError(axiosError.message);
    }
  }
  return translateEntraError(null);
}

function normalizeIdentifierForLogin(identifier: string): string {
  const trimmed = identifier.trim();
  if (trimmed.includes('@')) {
    return trimmed.toLowerCase();
  }
  return trimmed.replace(/\D/g, '');
}
